import { useContext } from 'react';
import { useStore } from 'zustand';
import { ChatContext } from './context';
import { ChatStore, ChatStoreState, ExtractState, InvalidStepIndex, StoreRef } from './types';
import { memoize, memoizeWithArgs } from 'proxy-memoize';
import {
  Attachment,
  ChatMessageDisplayType,
  ContextType,
  IndexedInteractionStepResponse,
  IndexedInteractionStepResponseForType,
  IndexedResponse,
  InteractionStepParams,
  InteractionStepResponse,
  InteractionStepType,
  MessageRole,
  MultiTurnContinueStepResponse,
  PromptInputStepResponse,
  TurnContinuationContext,
  TurnContinuationResponse,
  StepParamsForType,
  StepParamsForTypeWithStage,
  ProcessSupervisionStepParams,
} from '@scale/shared/types/chat';
import { ChatTaskParamsStages, RESPONSE_GENERATOR_STEPS } from '@scale/shared/chat/steps';
import { Message, MessageMetadata } from '@scale/shared/types/chat-completion';
import { get, groupBy, isNil } from 'lodash';
import {
  isStepVisible,
  isStepSequentiallySkippedAndVisible,
  isConditionallyVisible,
  producePromptWithReferenceTexts,
  getMostRecentModelSelectorResponseForTurn,
  constructChatHistory,
} from '@scale/shared/chat/api';
import { StepParamsAuxiliary } from './ChatStore.computed';

const NO_RESPONSE_PREFERENCE_DISCLAIMER =
  '**DISCLAIMER: The selected response is a placeholder for the chat history even though you selected no preference.**';

export const NO_ASSISTANT_RESPONSE_DISCLAIMER =
  '**DISCLAIMER: There is no model response in this turn. Expand this section to view the steps in the turn.**';

export const NO_ASSISTANT_RESPONSE_NO_VISIBLE_STEPS_DISCLAIMER =
  '**DISCLAIMER: This turn has no visible steps and no model response.**';

/**
 * Returns a chat store api from the chat store context
 * @returns Chat store api
 */
export function useChatStoreRef(): StoreRef {
  const chatStoreInstance = useContext(ChatContext);
  if (!chatStoreInstance?.current)
    throw Error('You have to wrap Chat component into ChatStoreContextProvider');
  return chatStoreInstance;
}

/**
 * Returns a selected part of the chat store.
 * @example const setResponses = useChatStoreExtract(state => state.setResponses)
 * @param selector A callback where you can specify which part of the store you need. It's a common practice to slim down to a single element you need.
 * @returns The selected part of the store
 */
export function useChatStoreExtract<U>(selector: (state: ExtractState<ChatStore>) => U): U {
  const chatStoreInstance = useContext(ChatContext);
  if (!chatStoreInstance?.current)
    throw Error('You have to wrap Chat component into ChatStoreContextProvider');
  return useStore(chatStoreInstance.current, selector);
}

export function getAllStepsCompleted(state: ChatStoreState) {
  return Object.values(state.completedSteps).every(e => e !== false);
}

export function useAllStepsCompleted() {
  return useChatStoreExtract<boolean>(state => getAllStepsCompleted(state));
}

const memoizedStepParams = memoizeWithArgs(
  <U extends InteractionStepType>(state: ChatStoreState, responseIndex: number) => {
    return state.stepParams[responseIndex] as StepParamsForTypeWithStage<U>;
  },
);

export function useStepParams<U extends InteractionStepType>(
  index: number,
): StepParamsForType<U> & StepParamsAuxiliary {
  return useChatStoreExtract<StepParamsForType<U> & StepParamsAuxiliary>(state =>
    memoizedStepParams(state, index),
  );
}

const memoizedStepParamsOrUndefined = memoizeWithArgs(
  <U extends InteractionStepType>(state: ChatStoreState, responseIndex: number | undefined) => {
    if (isNil(responseIndex)) return undefined;
    return state.stepParams[responseIndex] as StepParamsForTypeWithStage<U>;
  },
);

export function useStepParamsOrUndefined<U extends InteractionStepType>(
  index: number | undefined,
): (StepParamsForType<U> & StepParamsAuxiliary) | undefined {
  return useChatStoreExtract<(StepParamsForType<U> & StepParamsAuxiliary) | undefined>(state =>
    memoizedStepParamsOrUndefined(state, index),
  );
}

export function useCurrentStep() {
  return useChatStoreExtract<StepParamsForTypeWithStage<InteractionStepType> | undefined>(state =>
    state.stepParams.at(-1),
  );
}

export function useChatConfig() {
  return useChatStoreExtract<ChatStoreState['config']>(state => state.config);
}

const memoizedBeforeSteps = memoize(
  (state: ChatStoreState) =>
    state.stepParams.slice(
      0,
      state.config.before.length,
    ) as StepParamsForTypeWithStage<InteractionStepType>[],
);
export function useBeforeSteps(): StepParamsForTypeWithStage<InteractionStepType>[] {
  return useChatStoreExtract<StepParamsForTypeWithStage<InteractionStepType>[]>(state =>
    memoizedBeforeSteps(state),
  );
}

const memoizedTurnSteps = memoize((state: ChatStoreState) => {
  const turnLength = state.config.turn.length + 1;
  const turnParams = state.stepParams.filter(
    step => step.stage === ChatTaskParamsStages.Turn,
  ) as StepParamsForTypeWithStage<InteractionStepType>[];
  const turns: StepParamsForTypeWithStage<InteractionStepType>[][] = [];

  for (let i = 0; i < turnParams.length; i += turnLength) {
    const turn = turnParams.slice(i, i + turnLength);
    turns.push(turn);
  }
  return turns;
});

export function useTurnSteps(): StepParamsForTypeWithStage<InteractionStepType>[][] {
  return useChatStoreExtract<StepParamsForTypeWithStage<InteractionStepType>[][]>(state =>
    memoizedTurnSteps(state),
  );
}

const memoizedTurnIndexes = memoize((state: ChatStoreState) => {
  const turnLength = state.config.turn.length + 1;

  const turnParams = state.stepParams.filter(
    step => step.stage === ChatTaskParamsStages.Turn,
  ) as StepParamsForTypeWithStage<InteractionStepType>[];

  const turnIndexes: number[] = [];

  for (let i = 0; i < turnParams.length; i += turnLength) {
    turnIndexes.push(turnParams[i].turn);
  }
  return turnIndexes;
});
export function useTurnIndexes(): number[] {
  return useChatStoreExtract<number[]>(state => memoizedTurnIndexes(state));
}

const memoizedTurnByIndex = memoizeWithArgs(
  (state: ChatStoreState, turnIndex: number) =>
    state.stepParams.filter(
      step => step.stage === ChatTaskParamsStages.Turn && step.turn === turnIndex,
    ) as StepParamsForTypeWithStage<InteractionStepType>[],
);

export function useTurnByIndex(turn: number): StepParamsForTypeWithStage<InteractionStepType>[] {
  return useChatStoreExtract<StepParamsForTypeWithStage<InteractionStepType>[]>(state =>
    memoizedTurnByIndex(state, turn),
  );
}

const memoizedIsTurnCompleted = memoizeWithArgs((state: ChatStoreState, turn: number) => {
  const multiTurnContinueStep = state.stepParams.find(
    step =>
      step.stage === ChatTaskParamsStages.Turn &&
      step.turn === turn &&
      step.type === InteractionStepType.MultiTurnContinue,
  );
  if (!multiTurnContinueStep) return false;
  const multiTurnContinueStepResponse = state.responses.at(multiTurnContinueStep?.responseIndex);
  return (
    !isNil(multiTurnContinueStepResponse) &&
    multiTurnContinueStepResponse.contextType !== ContextType.Skipped
  );
});

export function useIsTurnCompleted(turn: number): boolean {
  return useChatStoreExtract<boolean>(state => memoizedIsTurnCompleted(state, turn));
}

export function useTurnMessages(turn: number): Message[] {
  return useChatStoreExtract<Message[]>(state => {
    const multiTurnContinueStep = state.stepParams.find(
      step =>
        step.stage === ChatTaskParamsStages.Turn &&
        step.turn === turn &&
        step.type === InteractionStepType.MultiTurnContinue,
    );
    if (!multiTurnContinueStep) return [];
    const lastResponse = state.responses.at(multiTurnContinueStep.responseIndex);
    if (!lastResponse) return [];
    const output = lastResponse.output as TurnContinuationResponse;
    const context = lastResponse.context as TurnContinuationContext;

    // TODO: We should eventually stop using context.message, and instead get the most recent non-skipped response generator step output
    // if context.message is empty because of a tie, use the first candidate response from the most recent selector step in the turn as a fallback
    const assistantContent: string = context.message.length
      ? context.message
      : getFallbackAssistantMessageContentForTurn(state.responses, turn, state.stepParams);
    const assistantMessage: Message = {
      role: MessageRole.Assistant,
      content: assistantContent,
      attachments: context.modelResponseAttachments,
      messageMetadata: context.modelResponseMetadata,
    };
    if (!context.shouldContinue) {
      return [assistantMessage];
    }
    const userMessage: Message = {
      role: context.role || MessageRole.User,
      content: output,
      attachments: context.attachments,
      skipMessage: context.skipPromptInput || false,
    };
    return [assistantMessage, userMessage];
  });
}

export function useTurnContinuationContext(turn: number) {
  return useChatStoreExtract<MultiTurnContinueStepResponse['context'] | undefined>(state => {
    const multiTurnContinueStep = state.stepParams.find(
      step =>
        step.stage === ChatTaskParamsStages.Turn &&
        step.turn === turn &&
        step.type === InteractionStepType.MultiTurnContinue,
    );
    if (!multiTurnContinueStep) return undefined;
    const lastResponse = state.responses.at(multiTurnContinueStep.responseIndex);
    if (!lastResponse) return undefined;
    return lastResponse.context;
  });
}

const memoizedMultiTurnContinueResponseForTurnIndex = memoizeWithArgs(
  (state: ChatStoreState, turn: number) => {
    const multiTurnContinueStepForTurn = state.stepParams.find(
      step =>
        step.stage === ChatTaskParamsStages.Turn &&
        step.turn === turn &&
        step.type === InteractionStepType.MultiTurnContinue,
    );
    if (!multiTurnContinueStepForTurn) return undefined;
    return state.responses.at(multiTurnContinueStepForTurn.responseIndex);
  },
);

export function useMultiTurnContinueResponseForTurnIndex(turn: number) {
  return useChatStoreExtract<IndexedResponse<InteractionStepResponse> | undefined>(state =>
    memoizedMultiTurnContinueResponseForTurnIndex(state, turn),
  );
}

export function useProcessSupervisionStep() {
  return useChatStoreExtract<ProcessSupervisionStepParams | undefined>(state => {
    const processSupervisionStep = state.config.turn.find(
      (step: InteractionStepParams): step is ProcessSupervisionStepParams =>
        [InteractionStepType.ProcessSupervision, InteractionStepType.ExperimentalToolUse].includes(
          step.type,
        ),
    );
    return processSupervisionStep;
  });
}

const memoizedAfterSteps = memoize(
  (state: ChatStoreState) =>
    state.stepParams.filter(
      step => step.stage === ChatTaskParamsStages.After,
    ) as StepParamsForTypeWithStage<InteractionStepType>[],
);

export function useAfterSteps(): StepParamsForTypeWithStage<InteractionStepType>[] {
  return useChatStoreExtract<StepParamsForTypeWithStage<InteractionStepType>[]>(state =>
    memoizedAfterSteps(state),
  );
}

const memoizedIsStepSequentiallySkippedAndVisible = memoizeWithArgs(
  (state: ChatStoreState, index: number) =>
    isStepSequentiallySkippedAndVisible(
      state.stepParams,
      state.responses,
      index,
      state.reviewLevel,
      state.config.templateVariables,
    ),
);

export function useIsStepSequentiallySkippedAndVisible(index: number): boolean {
  return useChatStoreExtract<boolean>(state =>
    memoizedIsStepSequentiallySkippedAndVisible(state, index),
  );
}

const memoizedIsCurrentStep = memoizeWithArgs((state: ChatStoreState, responseIndex: number) => {
  const currentStepIndex = state.responses.length;
  return currentStepIndex === responseIndex;
});

export function useIsCurrentStep(responseIndex: number) {
  return useChatStoreExtract<boolean>(state => memoizedIsCurrentStep(state, responseIndex));
}

const memoizedIsStepVisible = memoizeWithArgs((state: ChatStoreState, responseIndex: number) => {
  const stepParams = state.stepParams.at(responseIndex);
  if (!stepParams) return false;
  return isStepVisible(stepParams, state.reviewLevel);
});

export function useIsStepVisible(responseIndex: number) {
  return useChatStoreExtract<boolean>(state => memoizedIsStepVisible(state, responseIndex));
}

const isStepConditionallyVisible = (state: ChatStoreState, responseIndex: number) => {
  const stepParams = state.stepParams.at(responseIndex);
  if (!stepParams) return false;
  return isConditionallyVisible(
    stepParams,
    state.responses,
    responseIndex,
    state.config.templateVariables,
    state.reviewLevel,
  );
};

const memoizedIsStepConditionallyVisible = memoizeWithArgs(
  (state: ChatStoreState, responseIndex: number) =>
    isStepConditionallyVisible(state, responseIndex), // TODO: @Lajos refactor that check to be more clean
);

export function useIsStepConditionallyVisible(responseIndex: number) {
  return useChatStoreExtract<boolean>(state =>
    memoizedIsStepConditionallyVisible(state, responseIndex),
  );
}

/**
 * NOTE: We will return with undefined if the response is skipped.
 * If you want to include skipped responses, you can pass the includeSkipped flag.
 */
const memoizedStepResponseSelector = memoizeWithArgs(
  (state: ChatStoreState, index: number, includeSkipped?: boolean) => {
    const response = state.responses.at(index);
    if (!response || (!includeSkipped && response?.contextType === ContextType.Skipped))
      return undefined;
    return response;
  },
);

export function useStepResponse<U extends InteractionStepResponse>(
  index: number,
  includeSkipped?: boolean,
): IndexedResponse<U> | undefined {
  return useChatStoreExtract<IndexedResponse<U> | undefined>(
    state =>
      memoizedStepResponseSelector(state, index, includeSkipped ?? false) as
        | IndexedResponse<U>
        | undefined,
  );
}

const memoizedIsStepCompleted = memoizeWithArgs(
  (state: ChatStoreState, responseIndex: number, includeSkipped: boolean) => {
    let response = state.responses.at(responseIndex);
    if (!response || (!includeSkipped && response?.contextType === ContextType.Skipped)) {
      response = undefined;
    }
    const completedSteps = state.completedSteps;
    return response ? completedSteps[responseIndex] : false;
  },
);

export function useIsStepCompleted(responseIndex: number, includeSkipped?: boolean) {
  return useChatStoreExtract<boolean>(state =>
    memoizedIsStepCompleted(state, responseIndex, includeSkipped ?? false),
  );
}

export function usePromptInputDisplayType() {
  return useChatStoreExtract<ChatMessageDisplayType>(state => {
    const promptInputStep = state.config.before.find(
      step => step.type === InteractionStepType.PromptInput,
    );
    const displayType =
      get(promptInputStep, '[0].params.params.display_type') ??
      get(promptInputStep, 'params.display_type');
    return displayType ?? ChatMessageDisplayType.Markdown;
  });
}

const memoizedLastResponseGeneratorStep = memoize((state: ChatStoreState) =>
  state.config.turn.filter(step => RESPONSE_GENERATOR_STEPS.includes(step.type)).at(-1),
);

export function useLastResponseGeneratorStep() {
  return useChatStoreExtract<InteractionStepParams | undefined>(state =>
    memoizedLastResponseGeneratorStep(state),
  );
}

const memoizedIsAnyTurnIncomplete = memoize((state: ChatStoreState) => {
  const turns = groupBy(
    state.stepParams.filter(step => step.stage === ChatTaskParamsStages.Turn),
    ({ turn }) => turn,
  );
  return !Object.values(turns).every(turn => {
    const lastStep = turn.at(-1);
    return (
      !isNil(lastStep) &&
      lastStep.type === InteractionStepType.MultiTurnContinue &&
      !isNil(state.responses.at(lastStep.responseIndex))
    );
  });
});

export function useIsAnyTurnIncomplete() {
  return useChatStoreExtract<boolean>(state => memoizedIsAnyTurnIncomplete(state));
}

export function useHasIncompleteStepsInTurn(turn: number): boolean {
  return useChatStoreExtract<boolean>(state => {
    const turnIndexes = state.stepParams
      .filter(
        step =>
          step.stage === ChatTaskParamsStages.Turn &&
          step.turn === turn &&
          step.type !== InteractionStepType.MultiTurnContinue,
      )
      .map(step => step.responseIndex);

    return turnIndexes.some(index => !state.completedSteps[index]);
  });
}

export function useInvalidErrorMessagesForTurn(turn?: number): string[] {
  return useChatStoreExtract<string[]>(state => {
    if (isNil(turn)) {
      return [];
    }
    const stepFilter = (o: InvalidStepIndex) => o?.turnIndex === turn;
    const invalidErrorMessages = (state.invalidStepIndices ?? [])
      .filter(stepFilter)
      .map(o => o.errorMessage ?? 'This turn has some steps that are not completed.');

    return invalidErrorMessages;
  });
}

export function useLastTurnIndex(): number {
  return useChatStoreExtract<number>(state => {
    const lastTurnResponse = state.stepParams
      .filter(step => step.stage === ChatTaskParamsStages.Turn)
      .at(-1);
    return lastTurnResponse?.turn ?? 0;
  });
}

/*
 * NOTE: The following hooks were refactored from ThreadHistory to ChatStore
 * In the old implementation, we passed the history.until(responseIndex) to the components
 * We need to have the same functionality in the new implementation as well
 */

const memoizedNumberOfTurns = memoizeWithArgs((state: ChatStoreState, responseIndex?: number) => {
  const stepParams = !isNil(responseIndex)
    ? state.stepParams.slice(0, responseIndex + 1) // NOTE: +1 because we want to include the current response
    : state.stepParams;
  const lastTurnResponse = stepParams
    .filter(step => step.stage === ChatTaskParamsStages.Turn)
    .at(-1);
  const turnIndex = lastTurnResponse?.turn ?? 0;
  return turnIndex + 1;
});

export function useNumberOfTurns(responseIndex?: number): number {
  return useChatStoreExtract<number>(state => memoizedNumberOfTurns(state, responseIndex));
}

const memoizedMostRecentResponseInTurn = memoizeWithArgs((state: ChatStoreState, turn: number) => {
  const mostRecentResponseGeneratorStepInTurn = state.responses
    .filter(
      (response, i) =>
        state.stepParams[response.index]?.turn === turn &&
        response.contextType !== ContextType.Skipped &&
        RESPONSE_GENERATOR_STEPS.includes(response.type) &&
        isStepConditionallyVisible(state, i),
    )
    .at(-1);

  return mostRecentResponseGeneratorStepInTurn?.output ?? '';
});

export function useMostRecentResponseInTurn(turn: number) {
  return useChatStoreExtract<string>(state => memoizedMostRecentResponseInTurn(state, turn));
}

/**
 * Returns the most recent `response` object from the chat store
 * Breaks at the most recent PromptInput or MultiTurnContinue step
 */
const memoizedMostRecentResponseFullObject = memoizeWithArgs(
  (state: ChatStoreState, responseIndex?: number) => {
    const responses = !isNil(responseIndex)
      ? state.responses.slice(0, responseIndex)
      : state.responses;
    for (let ii = responses.length - 1; ii >= 0; ii--) {
      const response = responses[ii];

      if (
        response.contextType !== ContextType.Skipped &&
        RESPONSE_GENERATOR_STEPS.includes(response.type) &&
        isStepConditionallyVisible(state, ii)
      ) {
        return response;
      }
      if (
        [InteractionStepType.PromptInput, InteractionStepType.MultiTurnContinue].includes(
          response.type,
        )
      ) {
        break;
      }
    }
    return null;
  },
);

/**
 * Hook to get the most recent `response` object from the chat store
 */
export function useMostRecentResponseFullObject(responseIndex?: number) {
  return useChatStoreExtract<IndexedInteractionStepResponse | null>(state =>
    memoizedMostRecentResponseFullObject(state, responseIndex),
  );
}

/**
 * Memoized hook to get the most recent `response.messageMetadata` from the chat store
 */
export function useMostRecentMessageMetadata(responseIndex?: number): MessageMetadata | undefined {
  const response = useMostRecentResponseFullObject(responseIndex);
  if (response?.type === InteractionStepType.ModelResponseSelector) {
    let selectedIndex = 0;
    if (response.context?.selectedIndex >= 0) {
      selectedIndex = response.context?.selectedIndex;
    }
    const selectedCandidate = response.context?.candidates.at(selectedIndex);
    return selectedCandidate?.message.messageMetadata;
  }
  return response?.context?.messageMetadata;
}

/**
 * Memoized hook to get the most recent `response.output` from the chat store
 */
export function useMostRecentResponse(responseIndex?: number): string {
  return useMostRecentResponseFullObject(responseIndex)?.output ?? '';
}

// NOTE: Refactored from ThreadHistory.getMostRecentPrompt
export const memoizedMostRecentPrompt = memoizeWithArgs(
  (state: ChatStoreState, responseIndex?: number) => {
    const responses = !isNil(responseIndex)
      ? state.responses.slice(0, responseIndex)
      : state.responses;
    const config = state.config;
    const mostRecentPrompt = responses
      .filter(response =>
        [InteractionStepType.PromptInput, InteractionStepType.MultiTurnContinue].includes(
          response.type,
        ),
      )
      .at(-1);
    if (!mostRecentPrompt) return '';
    return producePromptWithReferenceTexts(mostRecentPrompt, config.useAppendedReferenceText);
  },
);

export function useMostRecentPrompt(responseIndex?: number) {
  return useChatStoreExtract<string>(state => memoizedMostRecentPrompt(state, responseIndex));
}

const memoizedMostRecentPromptForStepId = memoizeWithArgs(
  (state: ChatStoreState, responseIndex: number, stepId: string) => {
    const responses = state.responses.slice(0, responseIndex);
    const config = state.config;

    const mostRecentPromptForStepId = responses
      .filter(
        response =>
          [InteractionStepType.PromptInput, InteractionStepType.MultiTurnContinue].includes(
            response.type,
          ) && response.step_id === stepId,
      )
      .at(-1);

    if (!mostRecentPromptForStepId) return '';
    return producePromptWithReferenceTexts(
      mostRecentPromptForStepId,
      config.useAppendedReferenceText,
    );
  },
);

export function useMostRecentPromptForStepId(responseIndex: number, stepId?: string) {
  return useChatStoreExtract<string>(state => {
    if (!stepId) {
      return '';
    }
    return memoizedMostRecentPromptForStepId(state, responseIndex, stepId);
  });
}

// NOTE: Refactored from ThreadHistory.getMostRecentOutput
export function useMostRecentOutput(responseIndex?: number) {
  return useChatStoreExtract<string>(state => {
    const responses = !isNil(responseIndex)
      ? state.responses.slice(0, responseIndex)
      : state.responses;
    if (responses.length === 0) {
      return '';
    }
    return responses.at(-1)!.output;
  });
}

// NOTE: Refactored from ThreadHistory.getMostRecentOutputFullObject
export function useMostRecentOutputFullObject(responseIndex?: number) {
  return useChatStoreExtract<IndexedInteractionStepResponse | null>(state => {
    const responses = !isNil(responseIndex)
      ? state.responses.slice(0, responseIndex)
      : state.responses;
    if (responses.length === 0) {
      return null;
    }
    return responses.at(-1)!;
  });
}

// NOTE: Refactored from ThreadHistory.getMostRecentPromptIndex
export function useMostRecentPromptIndex(responseIndex?: number) {
  return useChatStoreExtract<number>(state => {
    const responses = !isNil(responseIndex)
      ? state.responses.slice(0, responseIndex)
      : state.responses;
    const mostRecentPrompt = responses
      .filter(response =>
        [InteractionStepType.PromptInput, InteractionStepType.MultiTurnContinue].includes(
          response.type,
        ),
      )
      .at(-1);
    return mostRecentPrompt?.index ?? -1;
  });
}
// NOTE: Refactored from ThreadHistory.getMostRecentPromptAttachments
export function useMostRecentPromptAttachments(untilResponseIndex?: number) {
  return useChatStoreExtract<Attachment[]>(state => {
    const responses = !isNil(untilResponseIndex)
      ? state.responses.slice(0, untilResponseIndex)
      : state.responses;
    const mostRecentPrompt = responses
      .filter(response =>
        [InteractionStepType.PromptInput, InteractionStepType.MultiTurnContinue].includes(
          response.type,
        ),
      )
      .at(-1);
    if (!mostRecentPrompt) {
      return [];
    }
    const attachments = (
      mostRecentPrompt as PromptInputStepResponse | MultiTurnContinueStepResponse
    ).context?.attachments;
    return attachments ?? [];
  });
}

// NOTE: Refactored from ThreadHistory.getMostRecentResponseForStepType
export function useMostRecentResponseForStepType<T extends InteractionStepType>(
  stepType: T,
  untilResponseIndex?: number,
) {
  return useChatStoreExtract<IndexedInteractionStepResponseForType<T> | null>(state => {
    const responses = !isNil(untilResponseIndex)
      ? state.responses.slice(0, untilResponseIndex)
      : state.responses;
    return (
      (responses
        .filter(
          (response, i) =>
            response.type === stepType &&
            response.contextType !== ContextType.Skipped &&
            isStepConditionallyVisible(state, i),
        )
        .at(-1) as IndexedInteractionStepResponseForType<T> | undefined) ?? null
    );
  });
}

// NOTE: Refactored from ThreadHistory.getAllResponsesForStepType
export function useAllResponsesForStepType<T extends InteractionStepType>(
  stepType: T,
  untilResponseIndex?: number,
) {
  return useChatStoreExtract<IndexedInteractionStepResponseForType<T>[]>(state => {
    const responsesUntilIndex = !isNil(untilResponseIndex)
      ? state.responses.slice(0, untilResponseIndex)
      : state.responses;
    return responsesUntilIndex.filter(
      response => response.type === stepType,
    ) as IndexedInteractionStepResponseForType<T>[];
  });
}

/*
 * NOTE: Refactored from ThreadHistory.getMostRecentStepParamsForStepType
 * NOTE: @Lajos Since step params doesn't change I think this hook doesn't make sense
 */
export function useMostRecentStepParamsForStepType<T extends InteractionStepType>(
  stepType: T,
  responseIndex?: number,
) {
  return useChatStoreExtract<StepParamsForTypeWithStage<T> | null>(state => {
    const responses = !isNil(responseIndex)
      ? state.responses.slice(0, responseIndex)
      : state.responses;

    const mostRecentResponse = responses
      .filter(
        response => response.type === stepType && response.contextType !== ContextType.Skipped,
      )
      .at(-1);
    if (!mostRecentResponse) {
      return null;
    }

    const stepParams = state.stepParams.at(mostRecentResponse.index);
    return (stepParams as StepParamsForTypeWithStage<T> | undefined) ?? null;
  });
}

// NOTE: Refactored from ThreadHistory.getMostRecentResponseForStepId
export function useMostRecentResponseForStepId(stepId?: string, untilResponseIndex?: number) {
  return useChatStoreExtract<IndexedInteractionStepResponse | null>(state => {
    if (!stepId) {
      return null;
    }

    const responses = !isNil(untilResponseIndex)
      ? state.responses.slice(0, untilResponseIndex)
      : state.responses;

    const mostRecentResponseForStepId = responses
      .filter(response => response.step_id === stepId)
      .at(-1);

    if (!mostRecentResponseForStepId) {
      return null;
    }

    return mostRecentResponseForStepId;
  });
}

// TODO (Matthew / Lajos): Deprecate in favor of emitting a clear local state event
const memoizedTurnVersion = memoizeWithArgs((state: ChatStoreState, turnIndex: number) => {
  return state.turnVersionHistory[turnIndex] ?? 0;
});

export function useTurnVersion(turnIndex: number) {
  return useChatStoreExtract<number>(state => {
    return memoizedTurnVersion(state, turnIndex);
  });
}

// NOTE: Refactored from ThreadHistory.getChatHistory
export function useChatHistory(responseIndex?: number) {
  return useChatStoreExtract<Message[]>(state => {
    return constructChatHistory({
      responses: state.responses,
      config: state.config,
      stepParamsWithStage: state.stepParams,
      responseIndex,
    });
  });
}

export const useProcessSupervisionLimitChatHistory = (responseIndex?: number) => {
  const chatHistory = useChatHistory(responseIndex);
  const prompt = chatHistory.find(message => message.role === MessageRole.User);
  const assistant = chatHistory.find(message => message.role === MessageRole.Assistant);
  const processSupervisionStep = useProcessSupervisionStep();
  return {
    isEnabled: processSupervisionStep?.params.limit_chat_history,
    promptRole: assistant ? MessageRole.Assistant : MessageRole.User,
    chatHistory: assistant ? [prompt!] : [],
  };
};

export const useSystemPrompt = () => {
  const chatHistory = useChatHistory();
  return chatHistory.find(message => message.role === MessageRole.System);
};

const memoizedIsRealCustomerAudit = memoize((state: ChatStoreState) => state.isRealCustomerAudit);

export function useIsRealCustomerAudit() {
  return useChatStoreExtract<boolean>(state => memoizedIsRealCustomerAudit(state) ?? false);
}

// Helper functions for the hooks
function getFallbackAssistantMessageContentForTurn(
  responses: IndexedInteractionStepResponse[],
  turn: number,
  stepParams: StepParamsForTypeWithStage<InteractionStepType>[],
): string {
  const content = getMostRecentModelSelectorResponseForTurn(responses, turn, stepParams);

  return content.length
    ? `${NO_RESPONSE_PREFERENCE_DISCLAIMER}\n\n${content}`
    : NO_ASSISTANT_RESPONSE_DISCLAIMER;
}
